import { HighlineService } from '@cof/auto-refinance-node-highline';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { DebugService } from '../shared/services/debug.service.js';
import { REFI_VEHICLE_LOAN_CONST } from '../shared/constants/app.constants.js';
import { POLICY_LIMITS_CONST } from '../shared/services/policy-limits.service.js';
import { CookieService } from '../core/services/cookie/cookie.service.js';
import { ApiService } from '../core/services/api/api.service.js';
import { LoanService } from './loan.service.js';
import { VehicleService } from './vehicle.service.js';
import { ApplicationService } from './application.service.js';
import { tags } from '../shared/constants/tags.js';
import { stratum } from '../../constants/stratum.js';
import { AppService } from '../app.service.js';

export class _VehicleAndLoanService {
  constructor() {
    this._vehicleList = [];
    this._loanList = [];
    this._vehicleAndLoanMatchList = [];
    this._showMatches = false;
    this._loading = false;
    this._hasConsent = false;
    this.debug = new DebugService('VehicleAndLoanService');
  }

  get vehicleList() {
    return this._vehicleList;
  }

  get loanList() {
    return this._loanList;
  }

  get vehicleAndLoanMatchList() {
    return this._vehicleAndLoanMatchList;
  }

  set applicationServiceVehicleAndLoanMatchList(vehicleAndLoanMatchList) {
    ApplicationService._vehicleAndLoanMatchList = vehicleAndLoanMatchList;
  }

  get showMatches() {
    return this._showMatches;
  }

  set showMatches(showMatches) {
    this._showMatches = showMatches;
  }

  set loading(status) {
    this._loading = status;
    this._loadingListener(status);
  }

  get loading() {
    return this._loading;
  }

  _loadingListener = () => {};

  registerLoadingListener = (listener) => {
    this._loadingListener = listener;
  };

  resetLists() {
    this._vehicleList = [];
    this._loanList = [];
    this._vehicleAndLoanMatchList = [];
    this.applicationServiceVehicleAndLoanMatchList = [];
  }

  createGetCustomerSSNBody(appModel) {
    const applicant = appModel?.primaryApplicant;
    const body = {};
    body.stateCode = applicant.currentAddress.stateCode;
    body.borrower = {
      address: {
        addressLine1: applicant.currentAddress.street,
        city: applicant.currentAddress.city,
        countryCode: 'US',
        postalCode: applicant.currentAddress.zipCode,
        stateCode: applicant.currentAddress.stateCode,
      },
      firstName: applicant.firstName,
      lastName: applicant.lastName,
      dateOfBirth: applicant.dateOfBirth,
      emailAddress: applicant.emailAddress,
      encryptedTaxId: applicant.ssn.replace(/-/g, ''),
    };
    if (appModel.applicationType.toLowerCase() === 'joint') {
      const { coApplicant } = appModel;
      body.coborrower = {
        address: {
          addressLine1: coApplicant.currentAddress.street,
          city: coApplicant.currentAddress.city,
          countryCode: 'US',
          postalCode: coApplicant.currentAddress.zipCode,
          stateCode: coApplicant.currentAddress.stateCode,
        },
        firstName: coApplicant.firstName,
        lastName: coApplicant.lastName,
        dateOfBirth: coApplicant.dateOfBirth,
        emailAddress: coApplicant.emailAddress,
        encryptedTaxId: coApplicant.ssn.replace(/-/g, ''), // where do we encrypt?
      };
    }

    return body;
  }

  async vehicleAndLoanLookup(lookupObj) {
    let lookupBody = {};
    const url = `${REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BFF_BASE_PATH}${REFI_VEHICLE_LOAN_CONST.BFF_SEARCH_ENDPOINT_MAP.vehicleAndLoanSearch}`;
    this.debug.log('vehicleAndLoanLookup called', lookupObj);
    lookupBody = this.createGetCustomerSSNBody(lookupObj);
    const priSsn = lookupBody.borrower.encryptedTaxId; // not encrypted
    const customHeaders = {
      headers: { ...REFI_VEHICLE_LOAN_CONST.HTTP_HEADERS },
      observe: 'response',
    };
    if (!priSsn) {
      customHeaders.headers = {
        ...REFI_VEHICLE_LOAN_CONST.HTTP_HEADERS,
        refiId: CookieService.refiId,
      };
    }
    const postRequest = () =>
      ApiService.post(url, lookupBody, customHeaders).then((res) => {
        // set ccid?
        const vehicleAndLoanList = res.vehicleAndLoanDetailsList;
        if (vehicleAndLoanList.length > 0) {
          ApiService.prequalId = res.refiPrequalId;
          return new Promise((resolve) =>
            resolve(this.parseVehicleAndLoanList(vehicleAndLoanList))
          );
        }
        return new Promise((resolve) => resolve([]));
      });
    if (!priSsn) {
      return postRequest();
    }
    // call tax id encryption
    if (!HighlineService.publicKey) {
      await HighlineService.getPublicKey(
        REFI_VEHICLE_LOAN_CONST.HIGHLINE.PRODUCT_ID()
      );
    }
    const encryptBorrowerTaxId = HighlineService.encryptWithPublicKey(
      lookupBody.borrower.encryptedTaxId
    );
    const encryptCoborrowerTaxId = HighlineService.encryptWithPublicKey(
      lookupBody.coborrower?.encryptedTaxId
        ? lookupBody.coborrower.encryptedTaxId
        : null
    );
    return Promise.all([encryptBorrowerTaxId, encryptCoborrowerTaxId]).then(
      (values) => {
        lookupBody.borrower.encryptedTaxId = values[0] ? values[0] : null;
        if (lookupBody.coborrower && lookupBody.coborrower.encryptedTaxId) {
          lookupBody.coborrower.encryptedTaxId = values[1] ? values[1] : null;
        }
        return postRequest();
      }
    );
  }

  parseVehicleAndLoanList(vehicleAndLoanList) {
    const updatedVehicleAndLoanList = [];
    const updatedVehicleList = [];
    const updatedLoanList = [];

    /* eslint-disable no-param-reassign */
    vehicleAndLoanList = this.validateLoanList(vehicleAndLoanList);
    vehicleAndLoanList = this.validateVehicleList(vehicleAndLoanList);

    vehicleAndLoanList.forEach((vehicleAndLoan) => {
      let hasValidVehicle = false;
      let hasValidLoan = false;

      if (
        vehicleAndLoan.vehicleDetails !== null &&
        vehicleAndLoan.vehicleDetails?.vin
      ) {
        updatedVehicleList.push(vehicleAndLoan.vehicleDetails);
        hasValidVehicle = true;
      }

      if (
        vehicleAndLoan.loanDetails !== null &&
        vehicleAndLoan.loanDetails?.lenderName
      ) {
        updatedLoanList.push(vehicleAndLoan.loanDetails);
        hasValidLoan = true;
      }

      if (hasValidVehicle && hasValidLoan) {
        updatedVehicleAndLoanList.push({
          vehicle: vehicleAndLoan.vehicleDetails,
          loan: vehicleAndLoan.loanDetails,
        });
      }
    });

    // sort and set
    this.sortIneligibleVehicleAndLoans(updatedVehicleAndLoanList);
    this.sortIneligibleVehicles(updatedVehicleList);
    this.sortIneligibleLoans(updatedLoanList);

    return updatedVehicleAndLoanList;
  }

  sortIneligibleVehicleAndLoans(vehicleAndLoanList) {
    this._vehicleAndLoanMatchList = vehicleAndLoanList.sort((a, b) => {
      let result =
        (this.isVehicleOrLoanEligible(a.vehicle) ? 0 : 1) -
        (this.isVehicleOrLoanEligible(b.vehicle) ? 0 : 1);
      result +=
        (this.isVehicleOrLoanEligible(a.loan) ? 0 : 1) -
        (this.isVehicleOrLoanEligible(b.loan) ? 0 : 1);
      if (result === 0) {
        result = parseInt(b.vehicle.year, 10) - parseInt(a.vehicle.year, 10);
      }
      return result;
    });
    this.applicationServiceVehicleAndLoanMatchList =
      this._vehicleAndLoanMatchList;
  }

  sortIneligibleVehicles(vehicles) {
    this._vehicleList = vehicles.sort((a, b) => {
      let result =
        (this.isVehicleOrLoanEligible(a) ? 0 : 1) -
        (this.isVehicleOrLoanEligible(b) ? 0 : 1);
      if (result === 0) {
        result = parseInt(b.year, 10) - parseInt(a.year, 10);
      }
      return result;
    });
  }

  sortIneligibleLoans(loans) {
    this._loanList = loans.sort((a, b) => {
      const result =
        (this.isVehicleOrLoanEligible(a) ? 0 : 1) -
        (this.isVehicleOrLoanEligible(b) ? 0 : 1);
      return result;
    });
  }

  isVehicleOrLoanEligible(vehicleOrLoan) {
    return vehicleOrLoan.ineligibleReasons
      ? vehicleOrLoan.ineligibleReasons.length === 0
      : true;
  }

  resetVehicleAndLoanService() {
    this.debug.log('resetting VehicleAndLoanService');
    this.lookupType = REFI_VEHICLE_LOAN_CONST.VEHICLE_LOOKUP_TYPE.SSN;
    this.resetLists();
    LoanService.resetLoanService();
    VehicleService.resetVehService();
  }

  validateLoanList(vehicleAndLoanList) {
    for (const vehicleAndLoan of vehicleAndLoanList) {
      if (vehicleAndLoan.loanDetails) {
        const {
          lenderName,
          currentBalance,
          remainingTerm,
          monthlyPayment,
          accountType,
        } = vehicleAndLoan.loanDetails;

        vehicleAndLoan.loanDetails.ineligibleReasons = [];

        if (lenderName && this.isCurrentCapitalOneAutoAccount(lenderName)) {
          vehicleAndLoan.loanDetails.ineligibleReasons.push('CAPITAL_ONE_LOAN');
        }

        if (lenderName && this.isIneligibleLender(lenderName)) {
          vehicleAndLoan.loanDetails.ineligibleReasons.push(
            'INELIGIBLE_LENDER'
          );
        }

        if (accountType && accountType === 'AL') {
          vehicleAndLoan.loanDetails.ineligibleReasons.push('AUTO_LEASE');
        }

        if (
          remainingTerm < POLICY_LIMITS_CONST.MIN_REMAINING_TERM() ||
          remainingTerm > POLICY_LIMITS_CONST.MAX_REMAINING_TERM()
        ) {
          vehicleAndLoan.loanDetails.ineligibleReasons.push('INELIGIBLE_TERMS');
        }

        if (currentBalance < POLICY_LIMITS_CONST.MIN_PAYOFF_AMOUNT()) {
          vehicleAndLoan.loanDetails.ineligibleReasons.push('MIN_AMOUNT');
        }

        if (currentBalance > LoanService.getMaximumBalanceAmount()) {
          vehicleAndLoan.loanDetails.ineligibleReasons.push('MAX_AMOUNT');
        }

        if (monthlyPayment > LoanService.getMaximumMonthlyPayment()) {
          vehicleAndLoan.loanDetails.ineligibleReasons.push('MONTHLY_PAYMENT');
        }
      }
    }
    return vehicleAndLoanList;
  }

  validateVehicleList(vehicleAndLoanList) {
    for (const vehicleAndLoan of vehicleAndLoanList) {
      if (vehicleAndLoan.vehicleDetails) {
        vehicleAndLoan.vehicleDetails.maxVehicleAge =
          POLICY_LIMITS_CONST.MAX_VEHICLE_AGE();
        vehicleAndLoan.vehicleDetails = VehicleService.validateVehicle(
          vehicleAndLoan.vehicleDetails
        );
      }
    }
    return vehicleAndLoanList;
  }

  isCurrentCapitalOneAutoAccount(lenderName) {
    const capitalOneLenderNamesSet = new Set(
      REFI_VEHICLE_LOAN_CONST.CAPITAL_ONE_LENDER_NAMES
    );
    return capitalOneLenderNamesSet.has(lenderName);
  }

  isIneligibleLender(lenderName) {
    const ineligibleLendertNamesSet = new Set(
      REFI_VEHICLE_LOAN_CONST.INELIGIBLE_LENDER_NAMES
    );
    return ineligibleLendertNamesSet.has(lenderName);
  }

  validateApplication(application) {
    this.application = application || ApplicationService.Application;
    if (this.application && this.application.primaryApplicant) {
      const applicant = this.application.primaryApplicant;
      if (
        applicant.firstName &&
        applicant.lastName &&
        applicant.dateOfBirth &&
        applicant.emailAddress &&
        applicant.currentAddress &&
        (applicant.ssn || ApplicationService.isIconicAndToggledOn)
      ) {
        const address = applicant.currentAddress;
        if (
          address.city &&
          address.zipCode &&
          address.stateCode &&
          address.street
        ) {
          return true;
        }
      }
    }
    return false;
  }

  async doVehicleAndLoanSearch(inputApplication) {
    if (
      this._vehicleAndLoanMatchList.length === 0 &&
      this._vehicleList.length === 0 &&
      this._loanList.length === 0 &&
      !AppService.testingParam
    ) {
      await this.searchVehicleAndLoan(inputApplication);
    } else if (AppService.testingParam) {
      switch (AppService.testingParam) {
        case 'VEHICLE_AND_LOAN':
          this.setMockVehicleAndLoanMatchResponse();
          break;
        case 'VEHICLE':
          this.setMockVehicleAndLoanNoMatchResponse();
          break;
        case 'LOAN': {
          this.setMockVehicleAndLoanNoMatchResponse();
          VehicleService.selectedVehicle = [...VehicleService.vehicleList];
          break;
        }
        case 'VEHICLE_LOOKUP':
          this.setMockVehicleAndLoanEmptyResponse();
          break;
        case 'MANUAL_LOAN': {
          this.setMockVehicleAndLoanEmptyResponse();
          VehicleService.selectedVehicle = {
            year: 2018,
            make: 'Hyundai',
            model: 'Sonata',
            trim: 'ST',
            wholeSaleValue: '18000.00',
            ineligibleReasons: null,
            informationalLog: null,
            maxVehicleAge: 10,
            vin: '1HGEJ864XXL045226',
            isEligible: true,
            isCapitalOneLoaned: false,
          };
          break;
        }
        default:
          break;
      }
    } else if (this._vehicleAndLoanMatchList.length === 0) {
      this.showMatches = false;
    } else {
      this.showMatches = true;
    }
  }

  async searchVehicleAndLoan(inputApplication = {}) {
    this.callingLexisNexis = true;
    this.loading = true;
    this.loadingText = REFI_VEHICLE_LOAN_CONST.SEARCH_TEXT;
    this.application =
      inputApplication && Object.keys(inputApplication).length !== 0
        ? inputApplication
        : ApplicationService.Application;
    await this.vehicleAndLoanLookup(this.application).then(
      async () => {
        this.debug.log('vehicle and loan call returned');
        REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
          tags.vehicleAndLoan.searchSuccess,
          '',
          'api',
          'message'
        );
        if (this._vehicleAndLoanMatchList.length > 0) {
          REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(tags.vehicleAndLoan.isMatchFound);
          stratum.publishTag(
            ApplicationService.isInAppDeclineDelayOn
              ? TagKey.VEHICLE_LOAN_VEHICLE_LOAN_MATCH_FOUND_2
              : TagKey.VEHICLE_LOAN_VEHICLE_LOAN_MATCH_FOUND
          );
        } else {
          REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(tags.vehicleAndLoan.noMatchFound);
          stratum.publishTag(
            ApplicationService.isInAppDeclineDelayOn
              ? TagKey.VEHICLE_LOAN_VEHICLE_LOAN_MATCH_NOT_FOUND_2
              : TagKey.VEHICLE_LOAN_VEHICLE_LOAN_MATCH_NOT_FOUND
          );
        }
        VehicleService.vehicleList = this._vehicleList;
        LoanService.loansList = this._loanList;

        if (
          VehicleService.vehicleList != null &&
          VehicleService.vehicleList.length > 0
        ) {
          stratum.publishTag(
            ApplicationService.isInAppDeclineDelayOn
              ? TagKey.VEHICLE_FOUND_LN_VEHICLE_FOUND_2
              : TagKey.VEHICLE_FOUND_LN_VEHICLE_FOUND
          );
          REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
            tags.vehicle.lexisVehiclesFound(VehicleService.vehicleList.length),
            '',
            'api',
            'message'
          );

          const eligibleVehicles = this.vehicleList.filter(
            (i) => i.ineligibleReasons?.length === 0
          );
          const ineligibleVehicles = this.vehicleList.filter(
            (i) => i.ineligibleReasons?.length !== 0
          );

          if (eligibleVehicles.length > 0) {
            REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
              tags.vehicle.lexisVehiclesEligible(eligibleVehicles.length),
              '',
              'api',
              'message'
            );
          }

          if (ineligibleVehicles.length > 0) {
            REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
              tags.vehicle.lexisVehiclesNotEligible(ineligibleVehicles.length),
              '',
              'api',
              'message'
            );
          }
        }
        if (LoanService.loansList != null && LoanService.loansList.length > 0) {
          stratum.publishTag(
            ApplicationService.isInAppDeclineDelayOn
              ? TagKey.TU_LOAN_SELECTION_TU_LOAN_FOUND_2
              : TagKey.TU_LOAN_SELECTION_TU_LOAN_FOUND
          );

          REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
            tags.loan.tuLoansFound(LoanService.loansList.length),
            '',
            'msg',
            'message'
          );

          let numOfEligibleLoans = 0;
          let numOfIneligibleLoans = 0;
          for (const loan of LoanService.loansList) {
            if (loan.ineligibleReasons.length === 0) {
              numOfEligibleLoans += 1;
            } else {
              numOfIneligibleLoans += 1;
            }

            if (numOfEligibleLoans > 1 && numOfIneligibleLoans > 1) {
              break;
            }
          }

          if (numOfEligibleLoans > 0) {
            REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
              tags.loan.loansEligible(numOfEligibleLoans),
              '',
              'msg',
              'message'
            );
          }

          if (numOfIneligibleLoans > 0) {
            REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
              tags.loan.loansNotEligible(numOfIneligibleLoans),
              '',
              'msg',
              'message'
            );
          }
        }

        if (this._vehicleAndLoanMatchList.length === 0) {
          this.showMatches = false;
        } else {
          this.showMatches = true;
        }
        this.callingLexisNexis = false;
        this.loading = false;
      },
      (err) => {
        this.debug.log('vehicle and loan call error', err);
        this.showMatches = false;

        this.callingLexisNexis = false;
        this.loading = false;
      }
    );
  }

  setMockVehicleAndLoanMatchResponse() {
    this.parseVehicleAndLoanList([
      {
        vehicleDetails: {
          year: 2018,
          make: 'Hyundai',
          model: 'Sonata',
          trim: 'ST',
          wholeSaleValue: '18000.00',
          ineligibleReasons: null,
          informationalLog: null,
          maxVehicleAge: 10,
          vin: '1HGEJ864XXL045226',
          isEligible: true,
          isCapitalOneLoaned: false,
        },
        loanDetails: {
          lenderName: 'HNTINGTN NTL',
          currentBalance: 13000,
          remainingTerm: 28,
          lastPaymentDate: '2019-03-16',
          monthlyPayment: 500,
          apr: 8.35,
          isEligible: true,
          ineligibleReasons: [],
          openedDate: '2016-03-12',
          lenderAccountNumber: '2774822206',
        },
      },
    ]);
    VehicleService.vehicleList = this._vehicleList;
    LoanService.loansList = this._loanList;
    this.showMatches = true;
  }

  setMockVehicleAndLoanNoMatchResponse() {
    this.parseVehicleAndLoanList([
      {
        vehicleDetails: {
          year: 2018,
          make: 'Hyundai',
          model: 'Sonata',
          trim: 'ST',
          wholeSaleValue: '18000.00',
          ineligibleReasons: null,
          informationalLog: null,
          maxVehicleAge: 10,
          vin: '1HGEJ864XXL045226',
          isEligible: true,
          isCapitalOneLoaned: false,
        },
        loanDetails: null,
      },
      {
        loanDetails: {
          lenderName: 'HNTINGTN NTL',
          currentBalance: 13000,
          remainingTerm: 28,
          lastPaymentDate: '2019-03-16',
          monthlyPayment: 500,
          apr: 8.35,
          isEligible: true,
          ineligibleReasons: [],
          openedDate: '2016-03-12',
          lenderAccountNumber: '2774822206',
        },
        vehicleDetails: null,
      },
    ]);
    VehicleService.vehicleList = this._vehicleList;
    LoanService.loansList = this._loanList;
    this.showMatches = false;
  }

  setMockVehicleAndLoanEmptyResponse() {
    this.showMatches = false;
    this.parseVehicleAndLoanList([]);
  }

  isMatchEligible(match) {
    if (
      !(
        match.vehicle.ineligibleReasons &&
        match.vehicle.ineligibleReasons.length > 0 &&
        Number(match.vehicle.wholeSaleValue) === 0
      )
    ) {
      if (
        this.isHighLtv(
          match.loan.currentBalance,
          match.vehicle.wholeSaleValue
        ) &&
        match.loan.ineligibleReasons.indexOf('HIGH_LTV') === -1
      ) {
        match.loan.ineligibleReasons.push('HIGH_LTV');
      }
      if (
        !this.isHighLtv(
          match.loan.currentBalance,
          match.vehicle.wholeSaleValue
        ) &&
        match.loan.ineligibleReasons.indexOf('HIGH_LTV') !== -1
      ) {
        match.loan.ineligibleReasons.splice(
          match.loan.ineligibleReasons.indexOf('HIGH_LTV'),
          1
        );
      }
    }
    return (
      !(
        match.vehicle.ineligibleReasons &&
        match.vehicle.ineligibleReasons.length > 0
      ) &&
      !(match.loan.ineligibleReasons && match.loan.ineligibleReasons.length > 0)
    );
  }

  isVehicleEligible(vehicle) {
    return !(vehicle.ineligibleReasons && vehicle.ineligibleReasons.length > 0);
  }

  isLoanEligible(loan) {
    return !(loan.ineligibleReasons && loan.ineligibleReasons.length > 0);
  }

  getInAppDeclineReason(vehicle, loan) {
    let declineReason;
    if (
      vehicle &&
      loan &&
      !(this.isVehicleEligible(vehicle) && this.isLoanEligible(loan))
    ) {
      const formattedVehicleIneligibleReasons = vehicle.ineligibleReasons
        ? [...vehicle.ineligibleReasons]
        : [];
      const formattedLoanIneligibleReasons = loan.ineligibleReasons
        ? [...loan.ineligibleReasons]
        : [];
      const combinedIneligibleReasons =
        formattedVehicleIneligibleReasons.concat(
          formattedLoanIneligibleReasons
        );

      if (combinedIneligibleReasons.length > 0) {
        declineReason = Object.keys(
          REFI_VEHICLE_LOAN_CONST.IN_APP_DECLINE_REASONS
        ).find((reason) =>
          reason === 'ATF'
            ? combinedIneligibleReasons.includes('MAX_AMOUNT') ||
              combinedIneligibleReasons.includes('MIN_AMOUNT')
            : combinedIneligibleReasons.includes(reason)
        );
      }
    }
    return declineReason;
  }

  isHighLtv(currentBalance, wholeSaleValue) {
    return (
      Number(currentBalance) / Number(wholeSaleValue) >
      POLICY_LIMITS_CONST.MAX_LTV()
    );
  }

  eligibleMatchCount() {
    let count = 0;
    for (const match of this._vehicleAndLoanMatchList) {
      if (this.isMatchEligible(match)) {
        count += 1;
      }
    }
    return count;
  }
}
export const VehicleAndLoanService = new _VehicleAndLoanService();
